import * as THREE from "three";
import sounds from "@/lib/sounds";

const SHIP_COLORS = ["#00F0FF", "#FF003C", "#8A2BE2", "#FFD700"];
const SHIP_COOLDOWN = [0.38, 0.32, 0.3, 0.26];
const ROWS = [
  { color: "#FF2BD6", pts: 50, kind: "squid" },
  { color: "#00F0FF", pts: 40, kind: "crab" },
  { color: "#FFE600", pts: 30, kind: "saucer" },
  { color: "#39FF14", pts: 20, kind: "beetle" },
  { color: "#FF7A00", pts: 10, kind: "crystal" },
];
const COLS = 10;
const RAINBOW = ["#FF3344", "#FF9900", "#FFE600", "#33FF66", "#3399FF", "#AA33FF"];

const lerp = (a, b, t) => a + (b - a) * Math.min(Math.max(t, 0), 1);

// Difficulty schedule from elapsed seconds
export function difficulty(t) {
  const move = t < 30 ? 1 + t * 0.006 : t < 60 ? 1.18 + (t - 30) * 0.01 : 1.48 + (t - 60) * 0.014;
  const fireRate = t < 30 ? 0 : t < 60 ? lerp(0.35, 1.0, (t - 30) / 30) : lerp(1.0, 4.2, (t - 60) / 120) + Math.max(0, t - 180) * 0.03;
  const diveEvery = t < 60 ? Infinity : lerp(6, 1.1, (t - 60) / 120);
  const diveSpeed = lerp(4, 9.5, (t - 60) / 120);
  const diverFire = lerp(1.6, 0.55, (t - 60) / 120);
  const bulletSpeed = t < 60 ? lerp(7, 9, (t - 30) / 30) : lerp(9, 15, (t - 60) / 120);
  return { move, fireRate, diveEvery, diveSpeed, diverFire, bulletSpeed };
}

function mat(color, intensity = 0.9) {
  return new THREE.MeshStandardMaterial({ color, emissive: color, emissiveIntensity: intensity, metalness: 0.3, roughness: 0.35 });
}

function buildAlien(kind, color) {
  const g = new THREE.Group();
  const m = mat(color);
  const eye = new THREE.MeshBasicMaterial({ color: "#ffffff" });
  const add = (geo, x, y, z = 0, material = m, rz = 0) => {
    const mesh = new THREE.Mesh(geo, material);
    mesh.position.set(x, y, z);
    mesh.rotation.z = rz;
    g.add(mesh);
    return mesh;
  };
  if (kind === "squid") {
    add(new THREE.OctahedronGeometry(0.5), 0, 0.1);
    for (let i = -1; i <= 1; i++) add(new THREE.CylinderGeometry(0.06, 0.02, 0.6, 6), i * 0.25, -0.45, 0, m, i * 0.3);
    add(new THREE.SphereGeometry(0.09, 8, 8), -0.16, 0.15, 0.4, eye);
    add(new THREE.SphereGeometry(0.09, 8, 8), 0.16, 0.15, 0.4, eye);
  } else if (kind === "crab") {
    add(new THREE.BoxGeometry(0.9, 0.5, 0.4), 0, 0);
    add(new THREE.BoxGeometry(0.2, 0.55, 0.2), -0.6, 0.2, 0, m, 0.5);
    add(new THREE.BoxGeometry(0.2, 0.55, 0.2), 0.6, 0.2, 0, m, -0.5);
    add(new THREE.BoxGeometry(0.12, 0.3, 0.12), -0.3, -0.38);
    add(new THREE.BoxGeometry(0.12, 0.3, 0.12), 0.3, -0.38);
    add(new THREE.BoxGeometry(0.14, 0.14, 0.1), -0.2, 0.08, 0.22, eye);
    add(new THREE.BoxGeometry(0.14, 0.14, 0.1), 0.2, 0.08, 0.22, eye);
  } else if (kind === "saucer") {
    const disc = add(new THREE.CylinderGeometry(0.7, 0.75, 0.18, 20), 0, -0.05);
    disc.rotation.x = 0.35;
    add(new THREE.SphereGeometry(0.32, 14, 10, 0, Math.PI * 2, 0, Math.PI / 2), 0, 0.05, 0, mat("#ffffff", 0.4));
    for (let i = 0; i < 5; i++) add(new THREE.SphereGeometry(0.06, 6, 6), -0.5 + i * 0.25, -0.12, 0.35, eye);
  } else if (kind === "beetle") {
    add(new THREE.TorusGeometry(0.42, 0.13, 10, 20), 0, 0);
    add(new THREE.SphereGeometry(0.26, 12, 12), 0, 0, 0, mat("#ffffff", 0.3));
    add(new THREE.ConeGeometry(0.08, 0.35, 6), -0.25, 0.5, 0, m, 0.4);
    add(new THREE.ConeGeometry(0.08, 0.35, 6), 0.25, 0.5, 0, m, -0.4);
  } else {
    add(new THREE.IcosahedronGeometry(0.45, 0), 0, 0);
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
      add(new THREE.ConeGeometry(0.09, 0.4, 5), Math.cos(a) * 0.5, Math.sin(a) * 0.5, 0, m, a - Math.PI / 2);
    }
  }
  return g;
}

function buildShip(type, color) {
  const g = new THREE.Group();
  const m = mat(color, 0.8);
  const body = new THREE.Mesh(new THREE.ConeGeometry(0.45, 1.4, 4), m);
  body.rotation.y = Math.PI / 4;
  g.add(body);
  const wing = new THREE.Mesh(new THREE.BoxGeometry(1.8 + type * 0.25, 0.22, 0.3), m);
  wing.position.y = -0.4;
  g.add(wing);
  if (type >= 1) {
    [-1, 1].forEach((s) => {
      const fin = new THREE.Mesh(new THREE.ConeGeometry(0.14, 0.6, 4), m);
      fin.position.set(s * (0.9 + type * 0.1), -0.2, 0);
      g.add(fin);
    });
  }
  if (type >= 3) {
    const ring = new THREE.Mesh(new THREE.TorusGeometry(0.75, 0.05, 6, 24), mat("#ffffff", 1));
    ring.rotation.x = Math.PI / 2;
    ring.position.y = -0.2;
    g.add(ring);
  }
  const cockpit = new THREE.Mesh(new THREE.SphereGeometry(0.16, 10, 10), new THREE.MeshBasicMaterial({ color: "#ffffff" }));
  cockpit.position.set(0, 0.05, 0.3);
  g.add(cockpit);
  return g;
}

export default class InvadersEngine {
  constructor(canvas, opts) {
    this.cvs = canvas;
    this.W = canvas.width;
    this.H = canvas.height;
    this.shipType = opts.shipType || 0;
    this.loadout = opts.loadout || {};
    this.shipColor = this.loadout.ship_color || SHIP_COLORS[this.shipType];
    this.cooldown = SHIP_COOLDOWN[this.shipType];
    this.onScore = opts.onScore || (() => {});
    this.onLives = opts.onLives || (() => {});
    this.onGameOver = opts.onGameOver || (() => {});
    this.keys = {};
    this.over = false;
    this.score = 0;
    this.lives = 3;
    this.elapsed = 0;
    this.wave = 0;
    this.lasers = [];
    this.alienBullets = [];
    this.particles = [];
    this.aliens = [];
    this.divers = [];
    this.lastShot = 0;
    this.fireAcc = 0;
    this.diveAcc = 0;
    this.inv = 0;
    this.shake = 0;
    this._setupScene();
    this._spawnWave();
    this._kd = (e) => {
      if (["Space", "ArrowLeft", "ArrowRight"].includes(e.code)) e.preventDefault();
      this.keys[e.code] = true;
    };
    this._ku = (e) => { this.keys[e.code] = false; };
  }

  _setupScene() {
    this.renderer = new THREE.WebGLRenderer({ canvas: this.cvs, antialias: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(this.W, this.H, false);
    this.renderer.setClearColor("#05040d");
    this.scene = new THREE.Scene();
    this.scene.fog = new THREE.Fog("#05040d", 30, 60);
    this.camera = new THREE.PerspectiveCamera(50, this.W / this.H, 0.1, 200);
    this.camera.position.set(0, -4, 30);
    this.camera.lookAt(0, 0, 0);
    this.halfH = 30 * Math.tan(THREE.MathUtils.degToRad(25)) - 1;
    this.halfW = this.halfH * (this.W / this.H);
    this.scene.add(new THREE.AmbientLight("#ffffff", 0.35));
    const key = new THREE.PointLight("#ffffff", 400, 80);
    key.position.set(0, 0, 20);
    this.scene.add(key);
    const starGeo = new THREE.BufferGeometry();
    const pos = [];
    const col = [];
    for (let i = 0; i < 700; i++) {
      pos.push((Math.random() - 0.5) * 120, (Math.random() - 0.5) * 80, -20 - Math.random() * 30);
      const c = new THREE.Color(RAINBOW[i % RAINBOW.length]).lerp(new THREE.Color("#ffffff"), 0.6);
      col.push(c.r, c.g, c.b);
    }
    starGeo.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
    starGeo.setAttribute("color", new THREE.Float32BufferAttribute(col, 3));
    this.stars = new THREE.Points(starGeo, new THREE.PointsMaterial({ size: 0.25, vertexColors: true }));
    this.scene.add(this.stars);
    this.player = buildShip(this.shipType, this.shipColor);
    this.playerY = -this.halfH + 3.2;
    this.player.position.set(0, this.playerY, 0);
    this.scene.add(this.player);
    this.particleGeo = new THREE.BoxGeometry(0.18, 0.18, 0.18);
  }

  _spawnWave() {
    this.wave++;
    this.aliens.forEach((a) => this.scene.remove(a.mesh));
    this.aliens = [];
    this.formX = 0;
    this.formY = this.halfH - 2.5;
    this.dir = 1;
    const spacingX = Math.min(2.1, (this.halfW * 1.5) / COLS);
    ROWS.forEach((row, r) => {
      for (let c = 0; c < COLS; c++) {
        const mesh = buildAlien(row.kind, row.color);
        const ox = (c - (COLS - 1) / 2) * spacingX;
        const oy = -r * 1.6;
        mesh.position.set(ox, this.formY + oy, 0);
        this.scene.add(mesh);
        this.aliens.push({ mesh, ox, oy, row: r, color: row.color, pts: row.pts, phase: Math.random() * 6 });
      }
    });
  }

  start() {
    window.addEventListener("keydown", this._kd);
    window.addEventListener("keyup", this._ku);
    this.prev = performance.now();
    this.raf = requestAnimationFrame((t) => this._loop(t));
  }

  stop() {
    cancelAnimationFrame(this.raf);
    window.removeEventListener("keydown", this._kd);
    window.removeEventListener("keyup", this._ku);
    this.scene.traverse((o) => {
      if (o.geometry) o.geometry.dispose();
      if (o.material) o.material.dispose();
    });
    this.renderer.dispose();
  }

  forceEnd() {
    if (this.over) return;
    this.over = true;
    this.onGameOver(this.score);
  }

  _loop(ts) {
    // Substep so game time keeps pace with real time on slow devices
    let left = Math.min((ts - this.prev) / 1000, 0.25);
    const dt = left;
    this.prev = ts;
    while (left > 0 && !this.over) {
      const step = Math.min(left, 0.033);
      left -= step;
      this.elapsed += step;
      this._update(step, ts);
    }
    this._animateFx(dt, ts);
    this.renderer.render(this.scene, this.camera);
    if (!this.over) this.raf = requestAnimationFrame((t) => this._loop(t));
  }

  _update(dt, ts) {
    const d = difficulty(this.elapsed);
    const speed = 7 + this.shipType * 0.6;
    if (this.keys.ArrowLeft) this.player.position.x -= speed * dt;
    if (this.keys.ArrowRight) this.player.position.x += speed * dt;
    this.player.position.x = Math.max(-this.halfW + 1, Math.min(this.halfW - 1, this.player.position.x));
    const tilt = (this.keys.ArrowRight ? -0.35 : 0) + (this.keys.ArrowLeft ? 0.35 : 0);
    this.player.rotation.y += (tilt - this.player.rotation.y) * 0.2;
    if (this.keys.Space && ts / 1000 - this.lastShot > this.cooldown) {
      this.lastShot = ts / 1000;
      this._firePlayer();
    }
    this._moveFormation(dt, d);
    this._moveDivers(dt, d);
    this._alienFire(dt, d);
    this._moveProjectiles(dt);
    this._collide();
    if (this.inv > 0) {
      this.inv -= dt;
      this.player.visible = Math.floor(this.inv * 10) % 2 === 0 || this.inv <= 0;
    }
  }

  _laserColor(i) {
    if (this.loadout.laser === "rainbow") return RAINBOW[(this.lasers.length + i) % RAINBOW.length];
    return this.shipColor;
  }

  _firePlayer() {
    sounds.shoot();
    const lt = this.loadout.laser;
    const offsets = lt === "dual" ? [-0.45, 0.45] : [0];
    const wide = lt === "wide";
    offsets.forEach((ox, i) => {
      const color = this._laserColor(i);
      const mesh = new THREE.Mesh(
        new THREE.CylinderGeometry(wide ? 0.22 : 0.09, wide ? 0.22 : 0.09, 0.9, 8),
        new THREE.MeshBasicMaterial({ color })
      );
      const glow = new THREE.Mesh(
        new THREE.SphereGeometry(wide ? 0.45 : 0.28, 8, 8),
        new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.35, blending: THREE.AdditiveBlending, depthWrite: false })
      );
      mesh.add(glow);
      mesh.position.set(this.player.position.x + ox, this.playerY + 0.8, 0);
      this.scene.add(mesh);
      this.lasers.push({ mesh, r: wide ? 0.85 : 0.6, glow });
    });
  }

  _moveFormation(dt, d) {
    const alive = this.aliens;
    if (alive.length === 0) {
      this._spawnWave();
      return;
    }
    const step = (1.6 + this.wave * 0.25) * d.move;
    this.formX += this.dir * step * dt;
    const xs = alive.map((a) => a.ox + this.formX);
    if (Math.max(...xs) > this.halfW - 0.8 && this.dir > 0) { this.dir = -1; this.formY -= 0.6; }
    if (Math.min(...xs) < -this.halfW + 0.8 && this.dir < 0) { this.dir = 1; this.formY -= 0.6; }
    let lowest = Infinity;
    alive.forEach((a) => {
      a.mesh.position.x = a.ox + this.formX;
      a.mesh.position.y = this.formY + a.oy + Math.sin(this.elapsed * 3 + a.phase) * 0.08;
      a.mesh.rotation.y = Math.sin(this.elapsed * 2 + a.phase) * 0.5;
      lowest = Math.min(lowest, a.mesh.position.y);
    });
    if (lowest < this.playerY + 1.4) {
      this._playerHit();
      this.formY = this.halfH - 2.5;
    }
  }

  _moveDivers(dt, d) {
    this.diveAcc += dt;
    if (this.diveAcc >= d.diveEvery && this.aliens.length > 6) {
      this.diveAcc = 0;
      const count = this.elapsed > 130 ? 2 : 1;
      for (let i = 0; i < count; i++) {
        const idx = Math.floor(Math.random() * this.aliens.length);
        const a = this.aliens.splice(idx, 1)[0];
        a.vx = (Math.random() - 0.5) * 2;
        a.fireCd = 0.4 + Math.random() * 0.6;
        a.mesh.scale.setScalar(1.15);
        this.divers.push(a);
      }
    }
    this.divers = this.divers.filter((a) => {
      const p = a.mesh.position;
      const toward = Math.sign(this.player.position.x - p.x) * 1.2;
      a.vx += (toward - a.vx) * dt * 0.8;
      p.x += (a.vx + Math.sin(this.elapsed * 4 + a.phase) * 1.5) * dt;
      p.y -= d.diveSpeed * dt;
      a.mesh.rotation.z += dt * 4;
      a.fireCd -= dt;
      if (a.fireCd <= 0 && p.y > this.playerY + 2) {
        a.fireCd = d.diverFire;
        this._fireAlien(p, d.bulletSpeed, a.color);
      }
      if (p.y < -this.halfH - 2) {
        this.scene.remove(a.mesh);
        return false;
      }
      return true;
    });
  }

  _alienFire(dt, d) {
    if (d.fireRate <= 0 || this.aliens.length === 0) return;
    this.fireAcc += dt * d.fireRate;
    while (this.fireAcc >= 1) {
      this.fireAcc -= 1;
      const cols = {};
      this.aliens.forEach((a) => {
        const k = Math.round(a.ox * 10);
        if (!cols[k] || a.mesh.position.y < cols[k].mesh.position.y) cols[k] = a;
      });
      const shooters = Object.values(cols);
      const s = shooters[Math.floor(Math.random() * shooters.length)];
      this._fireAlien(s.mesh.position, d.bulletSpeed, s.color);
    }
  }

  _fireAlien(from, speed, color) {
    sounds.alienShoot();
    const tx = this.player.position.x - from.x;
    const ty = this.playerY - from.y;
    const len = Math.hypot(tx, ty) || 1;
    const mesh = new THREE.Mesh(
      new THREE.OctahedronGeometry(0.22),
      new THREE.MeshBasicMaterial({ color })
    );
    const glow = new THREE.Mesh(
      new THREE.SphereGeometry(0.4, 8, 8),
      new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.3, blending: THREE.AdditiveBlending, depthWrite: false })
    );
    mesh.add(glow);
    mesh.position.set(from.x, from.y - 0.5, 0);
    this.scene.add(mesh);
    this.alienBullets.push({ mesh, vx: (tx / len) * speed, vy: (ty / len) * speed });
  }

  _moveProjectiles(dt) {
    this.lasers = this.lasers.filter((l) => {
      l.mesh.position.y += 22 * dt;
      if (l.mesh.position.y > this.halfH + 2) { this.scene.remove(l.mesh); return false; }
      return true;
    });
    this.alienBullets = this.alienBullets.filter((b) => {
      b.mesh.position.x += b.vx * dt;
      b.mesh.position.y += b.vy * dt;
      b.mesh.rotation.y += dt * 8;
      if (b.mesh.position.y < -this.halfH - 2) { this.scene.remove(b.mesh); return false; }
      return true;
    });
  }

  _collide() {
    const hitAlien = (list, isDiver) => {
      for (let i = list.length - 1; i >= 0; i--) {
        const a = list[i];
        const li = this.lasers.findIndex((l) => l.mesh.position.distanceTo(a.mesh.position) < l.r + 0.3);
        if (li < 0) continue;
        this.scene.remove(this.lasers[li].mesh);
        this.lasers.splice(li, 1);
        this.scene.remove(a.mesh);
        list.splice(i, 1);
        this._explode(a.mesh.position, a.color, 22);
        this.score += a.pts * (isDiver ? 2 : 1);
        this.onScore(this.score);
      }
    };
    hitAlien(this.aliens, false);
    hitAlien(this.divers, true);
    if (this.inv > 0) return;
    const pp = this.player.position;
    const bi = this.alienBullets.findIndex((b) => Math.abs(b.mesh.position.x - pp.x) < 0.9 && Math.abs(b.mesh.position.y - pp.y) < 0.7);
    if (bi >= 0) {
      this.scene.remove(this.alienBullets[bi].mesh);
      this.alienBullets.splice(bi, 1);
      this._playerHit();
      return;
    }
    const di = this.divers.findIndex((a) => a.mesh.position.distanceTo(pp) < 1.1);
    if (di >= 0) {
      const a = this.divers.splice(di, 1)[0];
      this.scene.remove(a.mesh);
      this._explode(a.mesh.position, a.color, 18);
      this._playerHit();
    }
  }

  _playerHit() {
    if (this.inv > 0 || this.over) return;
    this.lives--;
    this.inv = 2;
    this.shake = 0.5;
    this._explode(this.player.position, this.shipColor, 34);
    this.onLives(this.lives);
    if (this.lives <= 0) {
      this.over = true;
      this.player.visible = false;
      this.onGameOver(this.score);
    }
  }

  _explode(pos, color, n) {
    const style = this.loadout.explosion;
    const count = style === "firework" ? n * 2 : n;
    for (let i = 0; i < count; i++) {
      let c = color;
      if (style === "rainbow") c = RAINBOW[i % RAINBOW.length];
      else if (style === "firework") c = i % 3 === 0 ? "#FFD700" : color;
      const mesh = new THREE.Mesh(this.particleGeo, new THREE.MeshBasicMaterial({ color: c, transparent: true }));
      mesh.position.copy(pos);
      const a = Math.random() * Math.PI * 2;
      const sp = (style === "firework" ? 8 : 5) * (0.4 + Math.random());
      this.scene.add(mesh);
      this.particles.push({ mesh, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, vz: (Math.random() - 0.5) * sp, life: 0.7, max: 0.7 });
    }
    if (style === "plasma" || !style) {
      const ring = new THREE.Mesh(
        new THREE.RingGeometry(0.2, 0.45, 24),
        new THREE.MeshBasicMaterial({ color: style === "plasma" ? "#B388FF" : color, transparent: true, side: THREE.DoubleSide, blending: THREE.AdditiveBlending, depthWrite: false })
      );
      ring.position.copy(pos);
      this.scene.add(ring);
      this.particles.push({ mesh: ring, ring: true, life: 0.5, max: 0.5, vx: 0, vy: 0, vz: 0 });
    }
  }

  _animateFx(dt, ts) {
    this.particles = this.particles.filter((p) => {
      p.life -= dt;
      const k = Math.max(p.life / p.max, 0);
      if (p.ring) p.mesh.scale.setScalar(1 + (1 - k) * 6);
      else {
        p.mesh.position.x += p.vx * dt;
        p.mesh.position.y += p.vy * dt;
        p.mesh.position.z += p.vz * dt;
        p.mesh.rotation.x += dt * 6;
        p.mesh.scale.setScalar(k);
      }
      p.mesh.material.opacity = k;
      if (p.life <= 0) {
        this.scene.remove(p.mesh);
        p.mesh.material.dispose();
        if (p.ring) p.mesh.geometry.dispose();
        return false;
      }
      return true;
    });
    this.lasers.forEach((l) => l.glow.scale.setScalar(1 + Math.sin(ts / 40) * 0.2));
    this.stars.position.y = -((ts / 1000) * 1.5 % 20);
    if (this.shake > 0) {
      this.shake -= dt;
      this.camera.position.x = (Math.random() - 0.5) * this.shake * 1.5;
    } else this.camera.position.x = 0;
  }
}
